import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Descriptions,
    Drawer,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tag,
    Tooltip,
    message,
} from 'antd';
import {
    CheckCircleOutlined,
    PauseCircleOutlined,
    PlusOutlined,
    ReloadOutlined,
    RetweetOutlined,
} from '@ant-design/icons';
import PageHeader from '../../components/business/PageHeader';
import RequestState from '../../components/business/RequestState';
import StatusTag, { statusLabel } from '../../components/business/StatusTag';
import {
    BatchRecipient,
    EmailTemplateReference,
    MailAccount,
    MailBatch,
    RecipientListOwner,
    RecipientListReference,
} from '../../models/mail';
import { PageResult } from '../../models/v2';
import { V2ApiError, v2Api } from '../../services/v2';
import { asV2Error, currentAdminId, DateTimeValue, formatDateTime } from '../../utils/v2';
import {
    hasHumanReply,
    localizedMailError,
    mailFieldLabel,
    replyStatusOf,
    toIsoDateTime,
    validateMailRate,
} from './mailUi';
import { MAIL_PAGE_SIZE_OPTIONS, readMailPageSize, rememberMailPageSize } from './mailPagination';

const emptyBatches: PageResult<MailBatch> = {
    items: [], pageNo: 1, pageSize: readMailPageSize('batches', 20), total: 0,
};
const emptyRecipients: PageResult<BatchRecipient> = {
    items: [],
    pageNo: 1,
    pageSize: readMailPageSize('batch-recipients', 20),
    total: 0,
};
const batchStatuses = [
    'DRAFT',
    'PRECHECKED',
    'CONFIRMED',
    'SCHEDULED',
    'RUNNING',
    'PAUSED',
    'RECONCILING',
    'COMPLETED',
    'PARTIAL_FAILED',
    'CANCELLED',
];
type BatchAction = 'precheck' | 'confirm' | 'pause' | 'resume' | 'cancel';

const MailBatchesPage = () => {
    const [createForm] = Form.useForm();
    const [actionForm] = Form.useForm();
    const [data, setData] = useState(emptyBatches);
    const [filters, setFilters] = useState<any>({
        ownerId: currentAdminId(),
        pageNo: 1,
        pageSize: readMailPageSize('batches', 20),
    });
    const [detail, setDetail] = useState<MailBatch | null>(null);
    const [recipients, setRecipients] = useState(emptyRecipients);
    const [recipientFilters, setRecipientFilters] = useState<any>({
        pageNo: 1,
        pageSize: readMailPageSize('batch-recipients', 20),
    });
    const [recipientDomainDraft, setRecipientDomainDraft] = useState('');
    const [selectedRecipientRowKeys, setSelectedRecipientRowKeys] = useState<number[]>([]);
    const [loading, setLoading] = useState(true);
    const [detailLoading, setDetailLoading] = useState(false);
    const [error, setError] = useState<V2ApiError | null>(null);
    const [createVisible, setCreateVisible] = useState(false);
    const [action, setAction] = useState<BatchAction | null>(null);
    const [saving, setSaving] = useState(false);
    const [mailAccounts, setMailAccounts] = useState<MailAccount[]>([]);
    const [ownerOptions, setOwnerOptions] = useState<RecipientListOwner[]>([]);
    const [frozenListOptions, setFrozenListOptions] = useState<RecipientListReference[]>([]);
    const [templateOptions, setTemplateOptions] = useState<EmailTemplateReference[]>([]);
    const [canViewAllOwners, setCanViewAllOwners] = useState(false);
    const [ownerLoading, setOwnerLoading] = useState(false);
    const [optionLoading, setOptionLoading] = useState(false);

    const load = useCallback(
        async (silent = false) => {
            if (!silent) setLoading(true);
            setError(null);
            try {
                setData(await v2Api.mailBatches(filters));
            } catch (nextError) {
                setError(asV2Error(nextError));
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [filters]
    );

    const loadRecipients = useCallback(async (batchId: number, nextFilters: any) => {
        try {
            setRecipients(await v2Api.mailBatchRecipients(batchId, nextFilters));
        } catch (nextError) {
            message.error(asV2Error(nextError).message);
        }
    }, []);

    const openDetail = useCallback(
        async (id: number, silent = false) => {
            if (!silent) setDetailLoading(true);
            try {
                const next = await v2Api.mailBatch(id);
                setDetail(next);
                await loadRecipients(id, recipientFilters);
            } catch (nextError) {
                message.error(asV2Error(nextError).message);
                if (!silent) setDetail(null);
            } finally {
                if (!silent) setDetailLoading(false);
            }
        },
        [loadRecipients, recipientFilters]
    );

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        let mounted = true;
        setOwnerLoading(true);
        v2Api.recipientListOwners()
            .then((owners) => {
                if (!mounted) return;
                setOwnerOptions(owners.items || []);
                setCanViewAllOwners(owners.canViewAll);
                setFilters((current: any) => ({
                    ...current,
                    ownerId: current.ownerId || owners.currentActorId,
                    pageNo: 1,
                }));
            })
            .catch((nextError) => {
                if (mounted) message.error(asV2Error(nextError).message);
            })
            .finally(() => {
                if (mounted) setOwnerLoading(false);
            });
        return () => {
            mounted = false;
        };
    }, []);

    const detailId = detail && detail.id;

    useEffect(() => {
        if (detailId) loadRecipients(detailId, recipientFilters);
    }, [detailId, recipientFilters, loadRecipients]);

    useEffect(() => {
        setSelectedRecipientRowKeys([]);
    }, [
        detailId,
        recipientFilters.pageNo,
        recipientFilters.pageSize,
        recipientFilters.sendStatus,
        recipientFilters.failureCategory,
        recipientFilters.recipientDomain,
        recipientFilters.replyStatus,
    ]);

    useEffect(() => {
        const selectableIds = new Set(
            recipients.items
                .filter((row) => row.sendStatus === 'UNKNOWN')
                .map((row) => row.id)
        );
        setSelectedRecipientRowKeys((current) => {
            const next = current.filter((id) => selectableIds.has(id));
            return next.length === current.length ? current : next;
        });
    }, [recipients.items]);

    useEffect(() => {
        if (!detailId) return;
        const refresh = () => {
            if (document.hidden) return;
            openDetail(detailId, true);
            load(true);
        };
        const timer = window.setInterval(() => {
            refresh();
        }, 5000);
        window.addEventListener('focus', refresh);
        return () => {
            window.clearInterval(timer);
            window.removeEventListener('focus', refresh);
        };
    }, [detailId, openDetail, load]);

    useEffect(() => {
        const query = new URLSearchParams(window.location.hash.split('?')[1] || '');
        const open = Number(query.get('open'));
        if (open > 0) openDetail(open);
        // 页面首次加载只解析一次 hash。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadOwnerResources = async (ownerId: number) => {
        setOptionLoading(true);
        createForm.setFieldsValue({ recipientListVersionId: undefined, contentVersionId: undefined });
        try {
            const [lists, templates] = await Promise.all([
                v2Api.frozenRecipientListOptions(ownerId),
                v2Api.availableEmailContentOptions(ownerId),
            ]);
            setFrozenListOptions(lists || []);
            setTemplateOptions(templates || []);
        } catch (nextError) {
            setFrozenListOptions([]);
            setTemplateOptions([]);
            message.error(asV2Error(nextError).message);
        } finally {
            setOptionLoading(false);
        }
    };

    const openCreate = async () => {
        createForm.resetFields();
        setFrozenListOptions([]);
        setTemplateOptions([]);
        const local = new Date(Date.now() + 60_000 - new Date().getTimezoneOffset() * 60_000)
            .toISOString()
            .slice(0, 19);
        createForm.setFieldsValue({ plannedAt: local });
        setCreateVisible(true);
        setOptionLoading(true);
        try {
            const [owners, accounts] = await Promise.all([
                v2Api.recipientListOwners(),
                v2Api.activeMailAccountOptions(),
            ]);
            setOwnerOptions(owners.items || []);
            setCanViewAllOwners(owners.canViewAll);
            setMailAccounts(accounts || []);
            createForm.setFieldsValue({
                ownerId: owners.currentActorId,
                mailAccountId: !owners.canViewAll && accounts.length === 1 ? accounts[0].id : undefined,
            });
            await loadOwnerResources(owners.currentActorId);
        } catch (nextError) {
            setMailAccounts([]);
            message.error(asV2Error(nextError).message);
        } finally {
            setOptionLoading(false);
        }
    };

    const createBatch = async () => {
        const values = await createForm.validateFields();
        setSaving(true);
        try {
            const result = await v2Api.createMailBatch({
                sendMode: 'STANDALONE',
                ownerId: Number(values.ownerId),
                recipientListVersionId: Number(values.recipientListVersionId),
                contentVersionId: Number(values.contentVersionId),
                mailAccountId: Number(values.mailAccountId),
                plannedAt: toIsoDateTime(values.plannedAt),
                ratePerMinute: values.ratePerMinute ? Number(values.ratePerMinute) : undefined,
            });
            message.success('批次草稿已创建，请运行预检');
            setCreateVisible(false);
            createForm.resetFields();
            load();
            openDetail(result.id);
        } catch (nextError) {
            message.error(asV2Error(nextError).message);
        } finally {
            setSaving(false);
        }
    };

    const openAction = (nextAction: BatchAction) => {
        actionForm.resetFields();
        actionForm.setFieldsValue({
            ratePerMinute:
                nextAction === 'resume' ? Number(detail?.ratePerMinute || 1) : undefined,
            reason:
                nextAction === 'precheck'
                    ? '已核对名单、模板、账号与计划时间'
                    : nextAction === 'confirm'
                    ? '已复核预检结果，批准发送'
                    : '',
        });
        setAction(nextAction);
    };

    const submitAction = async () => {
        if (!detail || !action) return;
        const values = await actionForm.validateFields();
        setSaving(true);
        try {
            if (action === 'precheck') {
                await v2Api.precheckMailBatch(detail.id, {
                    version: Number(detail.version || 0),
                    reason: values.reason,
                });
                message.success(
                    detail.status === 'PAUSED'
                        ? '暂停批次预检已刷新，现在可以恢复发送'
                        : '预检已完成'
                );
            } else if (action === 'confirm') {
                if (!detail.precheckHash) throw new Error('预检哈希缺失，请重新运行预检');
                await v2Api.confirmMailBatch(detail.id, {
                    version: Number(detail.version || 0),
                    precheckHash: detail.precheckHash,
                    reason: values.reason,
                });
                message.success('批次已确认；到期后将进入发送队列');
            } else {
                await v2Api.controlMailBatch(detail.id, action, {
                    version: Number(detail.version || 0),
                    reason: values.reason,
                    ratePerMinute:
                        action === 'resume' ? Number(values.ratePerMinute) : undefined,
                });
                message.success(
                    `批次已${action === 'pause' ? '暂停' : action === 'resume' ? '恢复' : '取消'}`
                );
            }
            setAction(null);
            await openDetail(detail.id);
            load(true);
        } catch (nextError) {
            message.error(asV2Error(nextError).message);
        } finally {
            setSaving(false);
        }
    };

    const retryRecipient = (row: BatchRecipient) => {
        Modal.confirm({
            title: `重新发送给 ${row.personName || `收件人 #${row.id}`}？`,
            content: '系统会重新执行资格检查，并创建只含该用户的关联子批次；原批次结果不会被覆盖。',
            okText: '确认重新发送',
            onOk: async () => {
                try {
                    const result = await v2Api.retryMailRecipient(
                        row.batchId || detail!.id,
                        row.id,
                        {
                            version: Number(row.version || 0),
                            reason: '运营人员确认单收件人重新发送',
                        }
                    );
                    message.success(`已创建重发子批次 #${result.childBatchId}`);
                    loadRecipients(detail!.id, recipientFilters);
                } catch (nextError) {
                    message.error(asV2Error(nextError).message);
                }
            },
        });
    };

    const reconcileUnknownAsNotSent = (row: BatchRecipient) => {
        let reconciliationReason = '';
        Modal.confirm({
            title: `确认 ${row.personName || `收件人 #${row.id}`} 的邮件未发出？`,
            content: (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Alert
                        type="warning"
                        showIcon
                        message="仅当已有可靠证据确认未发出时执行；误判可能造成重复邮件。"
                    />
                    <Input.TextArea
                        maxLength={500}
                        rows={3}
                        placeholder="请填写核对依据，例如：服务商后台查询请求键无记录"
                        onChange={(event) => {
                            reconciliationReason = event.target.value;
                        }}
                    />
                </Space>
            ),
            okText: '确认未发送并重试',
            okButtonProps: { danger: true },
            onOk: async () => {
                const reason = reconciliationReason.trim();
                if (!reason) {
                    message.warning('请填写确认未发出的核对依据');
                    return Promise.reject();
                }
                try {
                    const result = await v2Api.reconcileUnknownMailRecipient(
                        row.batchId || detail!.id,
                        row.id,
                        {
                            version: Number(row.version || 0),
                            resolution: 'CONFIRMED_NOT_SENT',
                            reason,
                        }
                    );
                    message.success(
                        result.batchStatus === 'PAUSED'
                            ? '已加入重试队列；批次仍处于暂停状态，请确认后恢复批次'
                            : '已确认未发送并加入重试队列'
                    );
                    setSelectedRecipientRowKeys((current) =>
                        current.filter((id) => id !== row.id)
                    );
                    await loadRecipients(detail!.id, recipientFilters);
                    await openDetail(detail!.id);
                } catch (nextError) {
                    message.error(asV2Error(nextError).message);
                }
            },
        });
    };

    const reconcileSelectedUnknownAsNotSent = () => {
        const selectedRows = recipients.items.filter(
            (row) =>
                selectedRecipientRowKeys.includes(row.id) && row.sendStatus === 'UNKNOWN'
        );
        if (!selectedRows.length) {
            message.warning('请先选择结果未知的收件人');
            return;
        }
        let reconciliationReason = '';
        Modal.confirm({
            title: `批量确认 ${selectedRows.length} 封邮件未发出？`,
            content: (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Alert
                        type="warning"
                        showIcon
                        message="该操作会在同一事务中把所选 UNKNOWN 记录恢复到重试队列。请确保每一封邮件都有未发出的可靠证据。"
                    />
                    <Input.TextArea
                        maxLength={500}
                        rows={3}
                        placeholder="请填写批量核对依据"
                        onChange={(event) => {
                            reconciliationReason = event.target.value;
                        }}
                    />
                </Space>
            ),
            okText: `确认 ${selectedRows.length} 条并加入重试`,
            okButtonProps: { danger: true },
            onOk: async () => {
                const reason = reconciliationReason.trim();
                if (!reason) {
                    message.warning('请填写确认未发出的批量核对依据');
                    return Promise.reject();
                }
                try {
                    const result = await v2Api.reconcileUnknownMailRecipients(detail!.id, {
                        items: selectedRows.map((row) => ({
                            recipientId: row.id,
                            version: Number(row.version || 0),
                        })),
                        resolution: 'CONFIRMED_NOT_SENT',
                        reason,
                    });
                    setSelectedRecipientRowKeys([]);
                    message.success(
                        result.batchStatus === 'PAUSED'
                            ? `已处理 ${result.processedCount} 条；批次仍处于暂停状态`
                            : `已将 ${result.processedCount} 条记录加入重试队列`
                    );
                    await loadRecipients(detail!.id, recipientFilters);
                    await openDetail(detail!.id);
                } catch (nextError) {
                    message.error(asV2Error(nextError).message);
                }
            },
        });
    };

    const displayedRecipients = recipients.items;

    const applySummaryFilter = (sendStatus?: string) => {
        setSelectedRecipientRowKeys([]);
        setRecipientDomainDraft('');
        setRecipientFilters((current: any) => ({
            pageNo: 1,
            pageSize: current.pageSize,
            sendStatus,
        }));
        window.setTimeout(() => {
            document.getElementById('mail-batch-recipient-list')?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }, 0);
    };

    const batchColumns = [
        {
            title: '批次',
            key: 'batch',
            width: 230,
            render: (_: any, row: MailBatch) => (
                <Button type="link" className="v2-link-strong" onClick={() => openDetail(row.id)}>
                    {row.contentName || `发送批次 #${row.id}`}
                    <span className="v2-subline">#{row.id}</span>
                </Button>
            ),
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 120,
            render: (v: string) => <StatusTag value={v} />,
        },
        {
            title: '负责人',
            key: 'owner',
            width: 150,
            render: (_: any, row: MailBatch) => row.ownerName || row.ownerAccount || `#${row.ownerId}`,
        },
        {
            title: '计划发送',
            dataIndex: 'plannedAt',
            width: 180,
            render: (value?: DateTimeValue | null) => formatDateTime(value),
        },
        {
            title: '规模',
            key: 'count',
            width: 140,
            render: (_: any, row: MailBatch) =>
                `${row.estimatedCount || 0} 人 / 排除 ${row.excludedCount || 0}`,
        },
        {
            title: '回复人数',
            dataIndex: 'repliedCount',
            width: 200,
            render: (value?: number) => `${Number(value || 0)} 人`,
        },
        {
            title: '发送账号',
            key: 'account',
            width: 190,
            render: (_: any, row: MailBatch) => (
                <div>
                    {mailFieldLabel(row.providerCode, '其他邮件通道')}
                    <span className="v2-subline">
                        {row.sendingDomain || `账号 #${row.mailAccountId}`}
                    </span>
                </div>
            ),
        },
    ];

    const recipientColumns = [
        {
            title: '联系人',
            key: 'contact',
            width: 210,
            render: (_: any, row: BatchRecipient) => (
                <div>
                    <strong>{row.personName || `联系人 #${row.personId}`}</strong>
                    <span className="v2-subline">{row.companyName || '—'}</span>
                </div>
            ),
        },
        {
            title: '邮箱',
            dataIndex: 'emailAddress',
            width: 250,
            render: (v: string) => v || '—',
        },
        {
            title: '提交状态',
            dataIndex: 'sendStatus',
            width: 135,
            render: (v: string) => <StatusTag value={v} />,
        },
        {
            title: '投递状态',
            dataIndex: 'deliveryStatus',
            width: 135,
            render: (v: string) => <StatusTag value={v} />,
        },
        {
            title: '是否人工回复',
            key: 'hasHumanReply',
            width: 125,
            render: (_: any, row: BatchRecipient) =>
                hasHumanReply(row) ? <Tag color="green">是</Tag> : <Tag>否</Tag>,
        },
        {
            title: '回复状态 / 时间',
            key: 'replyStatus',
            width: 190,
            render: (_: any, row: BatchRecipient) => (
                <div>
                    <StatusTag value={replyStatusOf(row)} />
                    <span className="v2-subline">
                        {row.firstReplyAt
                            ? `首次 ${formatDateTime(row.firstReplyAt)}`
                            : '暂无首次回复'}
                    </span>
                    <span className="v2-subline">
                        {row.lastReplyAt
                            ? `最近 ${formatDateTime(row.lastReplyAt)}`
                            : '暂无最近回复'}
                    </span>
                </div>
            ),
        },
        {
            title: '发送尝试',
            key: 'attempt',
            width: 170,
            render: (_: any, row: BatchRecipient) => {
                const attempted = Number(row.attemptCount || 0);
                const maximum = 1 + Number(row.maxAdditionalRetriesSnapshot || 0);
                return (
                    <div>
                        已尝试 {attempted} 次
                        <span className="v2-subline">最多 {maximum} 次（含首次发送）</span>
                    </div>
                );
            },
        },
        {
            title: '退信原因',
            key: 'bounceReason',
            width: 240,
            render: (_: any, row: BatchRecipient) =>
                row.bounceCategory ? (
                    <div>
                        {mailFieldLabel(row.bounceCategory, '未识别退信')}
                        {row.bounceStatusCode && (
                            <span className="v2-subline">状态码：{row.bounceStatusCode}</span>
                        )}
                        {row.bounceDiagnostic && (
                            <span className="v2-subline">{row.bounceDiagnostic}</span>
                        )}
                        {row.bounceRetryScheduledAt && (
                            <span className="v2-subline">
                                自动重试：{formatDateTime(row.bounceRetryScheduledAt)}
                            </span>
                        )}
                    </div>
                ) : <span className="v2-muted">无</span>,
        },
        {
            title: '最近错误',
            key: 'lastError',
            width: 220,
            render: (_: any, row: BatchRecipient) => {
                const summary = localizedMailError(
                    row.lastErrorMessage,
                    row.lastErrorCode,
                    row.finalFailureCategory
                );
                const code = row.lastErrorMessage
                    ? row.finalFailureCategory || row.lastErrorCode
                    : undefined;
                return summary !== '—' ? (
                    <div>
                        {summary}
                        {code && (
                            <span className="v2-subline">
                                分类：{mailFieldLabel(code, '其他邮件错误')}
                            </span>
                        )}
                        {row.lastErrorMessage && row.lastErrorMessage !== summary && (
                            <span className="v2-subline">诊断：{row.lastErrorMessage}</span>
                        )}
                    </div>
                ) : <span className="v2-muted">无</span>;
            },
        },
        {
            title: '操作',
            key: 'actions',
            width: 110,
            fixed: 'right' as 'right',
            render: (_: any, row: BatchRecipient) => {
                const retryableFailure =
                    row.sendStatus === 'FAILED_FINAL' &&
                    ['RATE_LIMIT', 'PROVIDER_TEMPORARY', 'NETWORK_NOT_SENT'].includes(
                        row.finalFailureCategory || ''
                    );
                const retryableSoftBounce =
                    row.sendStatus === 'SUBMITTED' && row.deliveryStatus === 'SOFT_BOUNCE';
                const hardRestricted =
                    row.deliveryStatus === 'HARD_BOUNCE' ||
                    ['UNSUBSCRIBED', 'COMPLAINT'].includes(row.restrictionStatus || '');
                const retryable = !hardRestricted && (retryableFailure || retryableSoftBounce);
                const disabledReason =
                    row.sendStatus === 'UNKNOWN'
                        ? '结果未知，必须先完成对账'
                        : hardRestricted
                        ? '硬退信、投诉或退订不能重新发送'
                        : retryable
                        ? ''
                        : '当前状态或失败类型不允许重新发送';
                return row.sendStatus === 'UNKNOWN' ? (
                    <Button size="small" danger onClick={() => reconcileUnknownAsNotSent(row)}>
                        确认未发送
                    </Button>
                ) : (
                    <Tooltip title={disabledReason}>
                        <span>
                            <Button
                                size="small"
                                disabled={!retryable}
                                onClick={() => retryRecipient(row)}
                            >
                                重新发送
                            </Button>
                        </span>
                    </Tooltip>
                );
            },
        },
    ];

    const summary = detail?.recipientSummary || {};
    const summaryCards = [
        { label: '总任务', value: summary.totalCount || 0, sendStatus: undefined },
        { label: '待发送', value: summary.readyCount || 0, sendStatus: 'PENDING' },
        { label: '提交中', value: summary.submittingCount || 0, sendStatus: 'SUBMITTING' },
        { label: '已提交', value: summary.submittedCount || 0, sendStatus: 'SUBMITTED' },
        { label: '未知结果', value: summary.unknownCount || 0, sendStatus: 'UNKNOWN' },
        { label: '最终失败', value: summary.failedFinalCount || 0, sendStatus: 'FAILED_FINAL' },
        { label: '取消/跳过', value: summary.stoppedCount || 0, sendStatus: 'STOPPED' },
    ];
    const dispatchWaitNotice = (() => {
        if (detail?.status !== 'RUNNING') return null;
        if ((summary.dailyQuotaWaitingCount || 0) > 0 && !(summary.dueCount || 0)) {
            return {
                type: 'info' as const,
                message: '已达到发件账号当日发送上限',
                description: `剩余 ${summary.deferredCount || 0} 封将在账号时区的下一配额周期自动恢复，预计下次调度时间：${formatDateTime(
                    summary.nextDispatchAt
                )}。`,
            };
        }
        if ((summary.rateLimitedWaitingCount || 0) > 0 && !(summary.dueCount || 0)) {
            return {
                type: 'info' as const,
                message: '批次正在按发送速率等待',
                description: `剩余 ${summary.deferredCount || 0} 封将在 ${formatDateTime(
                    summary.nextDispatchAt
                )} 后继续调度。`,
            };
        }
        if ((summary.dueCount || 0) > 0 && !(summary.submittingCount || 0)) {
            return {
                type: 'warning' as const,
                message: '存在已到发送时间但尚未领取的邮件',
                description:
                    '后台调度器应在一分钟内继续处理；若数量持续不变，请检查服务端 Mail dispatch heartbeat 日志。',
            };
        }
        return null;
    })();
    return (
        <div className="v2-page mail-page">
            <PageHeader
                eyebrow="开发与培育 · 批量邮件"
                title="批量发送任务"
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={openCreate}
                    >
                        新建批次
                    </Button>
                }
            />
            <Card className="v2-filter-card" bordered={false}>
                <Space className="v2-wrap-space">
                    <span>负责人</span>
                    <Select
                        showSearch
                        optionFilterProp="children"
                        placeholder="请选择负责人"
                        style={{ width: 220 }}
                        value={filters.ownerId}
                        disabled={!canViewAllOwners}
                        loading={ownerLoading}
                        onChange={(ownerId: number) =>
                            setFilters({ ...filters, ownerId, pageNo: 1 })
                        }
                    >
                        {ownerOptions.map((owner) => (
                            <Select.Option key={owner.id} value={owner.id}>
                                {owner.realName || owner.account}（{owner.account}）
                            </Select.Option>
                        ))}
                    </Select>
                    <span>批次状态</span>
                    <Select
                        style={{ width: 180 }}
                        value={filters.status || 'ALL'}
                        onChange={(status: string) =>
                            setFilters({
                                ...filters,
                                status: status === 'ALL' ? undefined : status,
                                pageNo: 1,
                            })
                        }
                    >
                        <Select.Option value="ALL">全部状态</Select.Option>
                        {batchStatuses.map((status) => (
                            <Select.Option key={status} value={status}>
                                {statusLabel(status)}
                            </Select.Option>
                        ))}
                    </Select>
                    <Button icon={<ReloadOutlined />} onClick={() => load()}>
                        刷新
                    </Button>
                </Space>
            </Card>
            <Card className="v2-panel" bordered={false}>
                <RequestState
                    loading={loading}
                    error={error}
                    empty={!data.items.length}
                    onRetry={() => load()}
                >
                    <Table
                        rowKey="id"
                        columns={batchColumns}
                        dataSource={data.items}
                        scroll={{ x: 1050 }}
                        pagination={{
                            current: data.pageNo,
                            pageSize: data.pageSize,
                            total: data.total,
                            showSizeChanger: true,
                            pageSizeOptions: MAIL_PAGE_SIZE_OPTIONS,
                            showTotal: (total, range) =>
                                `显示第 ${range[0]} 到第 ${range[1]} 条，共 ${total} 条`,
                            onChange: (pageNo, pageSize) => {
                                rememberMailPageSize('batches', pageSize);
                                setFilters({ ...filters, pageNo, pageSize });
                            },
                        }}
                    />
                </RequestState>
            </Card>

            <Modal
                title="新建独立发送批次"
                visible={createVisible}
                onOk={createBatch}
                confirmLoading={saving}
                onCancel={() => setCreateVisible(false)}
                width={920}
            >
                <Alert
                    type="info"
                    showIcon
                    message="批次只使用已冻结名单和安全检查通过的模板版本；名单导入与治理请在发送名单页面完成。"
                />
                <Form form={createForm} layout="vertical">
                    <Row gutter={12}>
                        <Col span={24}>
                            <Form.Item name="ownerId" label="负责人" rules={[{ required: true, message: '请选择负责人' }]}>
                                <Select
                                    showSearch
                                    optionFilterProp="children"
                                    disabled={!canViewAllOwners}
                                    loading={optionLoading}
                                    placeholder="请选择负责人"
                                    onChange={(ownerId: number) => loadOwnerResources(ownerId)}
                                >
                                    {ownerOptions.map((owner) => (
                                        <Select.Option key={owner.id} value={owner.id}>
                                            {owner.realName || owner.account}（{owner.account}）
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item
                                name="recipientListVersionId"
                                label="冻结名单版本"
                                rules={[{ required: true, message: '请选择冻结名单版本' }]}
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="children"
                                    loading={optionLoading}
                                    placeholder="请选择已冻结版本"
                                    notFoundContent={optionLoading ? '正在加载…' : '该负责人暂无可选冻结版本'}
                                >
                                    {frozenListOptions.map((item) => (
                                        <Select.Option key={item.versionId} value={item.versionId}>
                                            {item.listName || `名单 #${item.listId}`} · V{item.versionNo || 1}（可发送 {item.eligibleCount || 0}）
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="contentVersionId"
                                label="模板版本"
                                rules={[{ required: true, message: '请选择模板版本' }]}
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="children"
                                    loading={optionLoading}
                                    placeholder="请选择安全检查通过的模板版本"
                                    notFoundContent={optionLoading ? '正在加载…' : '该负责人暂无可选模板版本'}
                                >
                                    {templateOptions.map((item) => (
                                        <Select.Option key={item.versionId} value={item.versionId}>
                                            {item.contentName || `模板 #${item.contentId}`} · V{item.versionNo || 1}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item
                                name="mailAccountId"
                                label="发件邮箱账号"
                                rules={[{ required: true, message: '请选择已验证的发件账号' }]}
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="children"
                                    loading={optionLoading}
                                    placeholder="请选择已验证的发件账号"
                                    notFoundContent={optionLoading ? '正在加载…' : '暂无权限范围内的可用邮箱账号'}
                                >
                                    {mailAccounts.map((account) => (
                                        <Select.Option key={account.id} value={account.id}>{`${
                                            account.senderDisplayName || account.providerAccountRef
                                        }（${account.fromAddress}）`}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="plannedAt"
                                label="计划时间"
                                rules={[{ required: true }]}
                            >
                                <Input type="datetime-local" step={1} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={12}>
                        <Col span={24}>
                            <Form.Item name="ratePerMinute" label="每分钟速率">
                                <InputNumber min={1} max={100000} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            <Drawer
                title={detail ? `${detail.contentName || '发送批次'} · #${detail.id}` : '批次详情'}
                width="min(1280px, 98vw)"
                visible={!!detail}
                onClose={() => setDetail(null)}
                destroyOnClose
            >
                {detailLoading || !detail ? (
                    <Card loading />
                ) : (
                    <>
                        <div className="mail-batch-actions">
                            <Space className="v2-wrap-space">
                                <StatusTag value={detail.status} />
                                <Button
                                    disabled={
                                        !['DRAFT', 'PRECHECKED', 'PAUSED'].includes(
                                            detail.status || ''
                                        )
                                    }
                                    onClick={() => openAction('precheck')}
                                    icon={<CheckCircleOutlined />}
                                >
                                    运行预检
                                </Button>
                                <Button
                                    type="primary"
                                    disabled={
                                        detail.status !== 'PRECHECKED' || !detail.precheckHash
                                    }
                                    onClick={() => openAction('confirm')}
                                >
                                    确认发送
                                </Button>
                                <Button
                                    disabled={
                                        !['CONFIRMED', 'SCHEDULED', 'RUNNING'].includes(
                                            detail.status || ''
                                        )
                                    }
                                    onClick={() => openAction('pause')}
                                    icon={<PauseCircleOutlined />}
                                >
                                    暂停
                                </Button>
                                <Button
                                    disabled={detail.status !== 'PAUSED'}
                                    onClick={() => openAction('resume')}
                                    icon={<RetweetOutlined />}
                                >
                                    恢复
                                </Button>
                                <Button
                                    danger
                                    disabled={[
                                        'COMPLETED',
                                        'PARTIAL_FAILED',
                                        'CANCELLED',
                                    ].includes(detail.status || '')}
                                    onClick={() => openAction('cancel')}
                                >
                                    取消
                                </Button>
                            </Space>
                        </div>
                        {dispatchWaitNotice && (
                            <Alert
                                style={{ marginBottom: 12 }}
                                showIcon
                                type={dispatchWaitNotice.type}
                                message={dispatchWaitNotice.message}
                                description={dispatchWaitNotice.description}
                            />
                        )}
                        <Row gutter={12} className="v2-stat-grid">
                            {summaryCards.map(({ label, value, sendStatus }) => {
                                const active =
                                    recipientFilters.sendStatus === sendStatus &&
                                    !recipientFilters.failureCategory &&
                                    !recipientFilters.recipientDomain &&
                                    !recipientFilters.replyStatus;
                                return (
                                    <Col xs={12} md={6} lg={3} key={label}>
                                        <Card
                                            hoverable
                                            className={
                                                active
                                                    ? 'v2-stat-card is-active'
                                                    : 'v2-stat-card'
                                            }
                                            onClick={() => applySummaryFilter(sendStatus)}
                                        >
                                            <Statistic title={label} value={value} />
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                        <Descriptions bordered size="small" column={2}>
                            <Descriptions.Item label="计划时间">
                                {formatDateTime(detail.plannedAt)}
                            </Descriptions.Item>
                            <Descriptions.Item label="实际速率">
                                {detail.ratePerMinute || '账号默认'} / 分钟
                            </Descriptions.Item>
                            <Descriptions.Item label="名单版本">
                                #{detail.recipientListVersionId}
                            </Descriptions.Item>
                            <Descriptions.Item label="模板版本">
                                #{detail.contentVersionId}
                            </Descriptions.Item>
                            <Descriptions.Item label="邮件账号">
                                #{detail.mailAccountId} · {mailFieldLabel(detail.providerCode, '其他邮件通道')}
                            </Descriptions.Item>
                            <Descriptions.Item label="发送域">
                                {detail.sendingDomain || '—'}
                            </Descriptions.Item>
                        </Descriptions>
                        <Card
                            id="mail-batch-recipient-list"
                            className="v2-section"
                            title="收件人明细"
                            bordered={false}
                        >
                            <Space className="mail-table-filter v2-wrap-space">
                                <Select
                                    allowClear
                                    placeholder="提交状态"
                                    style={{ width: 165 }}
                                    value={recipientFilters.sendStatus}
                                    onChange={(sendStatus) =>
                                        setRecipientFilters({
                                            ...recipientFilters,
                                            sendStatus,
                                            pageNo: 1,
                                        })
                                    }
                                >
                                    <Select.Option value="PENDING">待发送（含排队和等待重试）</Select.Option>
                                    <Select.Option value="STOPPED">取消/跳过</Select.Option>
                                    {[
                                        'READY',
                                        'LEASED',
                                        'SUBMITTING',
                                        'SUBMITTED',
                                        'UNKNOWN',
                                        'FAILED_RETRYABLE',
                                        'FAILED_FINAL',
                                        'SKIPPED',
                                        'CANCELLED',
                                    ].map((status) => (
                                        <Select.Option key={status} value={status}>
                                            {statusLabel(status)}
                                        </Select.Option>
                                    ))}
                                </Select>
                                <Select
                                    allowClear
                                    placeholder="失败分类"
                                    style={{ width: 190 }}
                                    value={recipientFilters.failureCategory}
                                    onChange={(failureCategory) =>
                                        setRecipientFilters({
                                            ...recipientFilters,
                                            failureCategory,
                                            pageNo: 1,
                                        })
                                    }
                                >
                                    {[
                                        'RATE_LIMIT',
                                        'PROVIDER_TEMPORARY',
                                        'NETWORK_NOT_SENT',
                                        'AMBIGUOUS_SUBMISSION',
                                        'AUTHENTICATION',
                                        'PERMISSION_OR_QUOTA_FINAL',
                                        'CONTENT_REJECTED',
                                        'ADDRESS_PERMANENT',
                                        'POLICY_DENIED',
                                    ].map((category) => (
                                        <Select.Option key={category} value={category}>
                                            {mailFieldLabel(category, '其他失败类型')}
                                        </Select.Option>
                                    ))}
                                </Select>
                                <Select
                                    allowClear
                                    placeholder="回复状态"
                                    style={{ width: 185 }}
                                    value={recipientFilters.replyStatus}
                                    onChange={(replyStatus) =>
                                        setRecipientFilters({
                                            ...recipientFilters,
                                            replyStatus,
                                            pageNo: 1,
                                        })
                                    }
                                >
                                    <Select.Option value="NONE">未回复</Select.Option>
                                    <Select.Option value="HUMAN_REPLIED">人工回复</Select.Option>
                                    <Select.Option value="AUTO_REPLIED">自动回复</Select.Option>
                                </Select>
                                <Input
                                    placeholder="收件域，回车筛选"
                                    style={{ width: 220 }}
                                    value={recipientDomainDraft}
                                    onChange={(event) => setRecipientDomainDraft(event.target.value)}
                                    onPressEnter={(event) =>
                                        setRecipientFilters({
                                            ...recipientFilters,
                                            recipientDomain: (event.target as HTMLInputElement)
                                                .value,
                                            pageNo: 1,
                                        })
                                    }
                                />
                            </Space>
                            <Space style={{ margin: '12px 0' }} className="v2-wrap-space">
                                <Button
                                    onClick={() =>
                                        setSelectedRecipientRowKeys(
                                            displayedRecipients
                                                .filter((row) => row.sendStatus === 'UNKNOWN')
                                                .map((row) => row.id)
                                        )
                                    }
                                    disabled={
                                        !displayedRecipients.some(
                                            (row) => row.sendStatus === 'UNKNOWN'
                                        )
                                    }
                                >
                                    全选当前页 UNKNOWN
                                </Button>
                                <Button
                                    onClick={() => setSelectedRecipientRowKeys([])}
                                    disabled={!selectedRecipientRowKeys.length}
                                >
                                    取消全选
                                </Button>
                                <Button
                                    type="primary"
                                    danger
                                    disabled={!selectedRecipientRowKeys.length}
                                    onClick={reconcileSelectedUnknownAsNotSent}
                                >
                                    批量确认未发出（{selectedRecipientRowKeys.length}）
                                </Button>
                            </Space>
                            <Table
                                rowKey="id"
                                columns={recipientColumns}
                                dataSource={displayedRecipients}
                                rowSelection={{
                                    selectedRowKeys: selectedRecipientRowKeys,
                                    onChange: (keys) =>
                                        setSelectedRecipientRowKeys(
                                            keys.map((key) => Number(key))
                                        ),
                                    getCheckboxProps: (row: BatchRecipient) => ({
                                        disabled: row.sendStatus !== 'UNKNOWN',
                                        name: String(row.id),
                                    }),
                                }}
                                scroll={{ x: 1250 }}
                                pagination={{
                                    current: recipients.pageNo,
                                    pageSize: recipients.pageSize,
                                    total: recipients.total,
                                    showSizeChanger: true,
                                    pageSizeOptions: MAIL_PAGE_SIZE_OPTIONS,
                                    showTotal: (total) => `共 ${total} 个收件人`,
                                    onChange: (pageNo, pageSize) => {
                                        rememberMailPageSize('batch-recipients', pageSize);
                                        setRecipientFilters({
                                            ...recipientFilters,
                                            pageNo,
                                            pageSize,
                                        });
                                    },
                                }}
                            />
                        </Card>
                    </>
                )}
            </Drawer>

            <Modal
                title={
                    action === 'precheck'
                        ? '运行批次预检'
                        : action === 'confirm'
                        ? '确认正式发送'
                        : action === 'pause'
                        ? '暂停批次'
                        : action === 'resume'
                        ? '恢复批次'
                        : '取消批次'
                }
                visible={!!action}
                zIndex={1200}
                onOk={submitAction}
                confirmLoading={saving}
                onCancel={() => setAction(null)}
                okButtonProps={{ danger: action === 'cancel' }}
            >
                {action === 'confirm' && (
                    <Alert
                        type="warning"
                        showIcon
                        message="确认后会生成逐收件人任务；已经提交到服务商的邮件无法撤回，请再次核对名单、模板、账号和计划时间。"
                    />
                )}
                <Form form={actionForm} layout="vertical">
                    {action === 'resume' && (
                        <>
                            <Alert
                                type="info"
                                showIcon
                                message="恢复前可调整本批次发送速率"
                                description={`修改后立即用于剩余待发送邮件；不得超过当前发件账号上限 ${
                                    detail?.accountRatePerMinute || '—'
                                } 封/分钟。`}
                            />
                            <Form.Item
                                name="ratePerMinute"
                                label="恢复后的发送速率（封/分钟）"
                                rules={[
                                    { required: true, message: '请输入恢复后的发送速率' },
                                    {
                                        validator: (_rule, value) => {
                                            const error = validateMailRate(
                                                value,
                                                detail?.accountRatePerMinute || 100000
                                            );
                                            return error
                                                ? Promise.reject(new Error(error))
                                                : Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <InputNumber
                                    min={1}
                                    max={Number(detail?.accountRatePerMinute || 100000)}
                                    precision={0}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </>
                    )}
                    <Form.Item
                        name="reason"
                        label="操作理由"
                        rules={[{ required: true, whitespace: true }]}
                    >
                        <Input.TextArea rows={3} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default MailBatchesPage;
